// eslint-disable-next-line
import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import { withFirebase, isLoaded, isEmpty } from "react-redux-firebase";
import { compose } from "redux";
import { connect } from "react-redux";
import GoogleButton from "react-google-button";
import "./LoginPage.scss";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LogRocket from "logrocket";

import * as UserActions from "../actions/UserActions";

const enhance = compose(
  withRouter,
  withFirebase,
  connect(
    ({ firebase: { auth }, user: { isWhitelisted, email } }) => ({
      auth,
      isWhitelisted,
      whitelistEmail: email,
    }),
    (dispatch, ownProps) => ({
      updateCustomClaims: () => {
        const { firebase } = ownProps;
        const user = firebase.auth().currentUser;
        if (!user) return Promise.resolve();
        return user.getIdTokenResult(true).then((idTokenResult) => {
          const isWhitelisted = idTokenResult.claims.isWhitelisted;
          const email = idTokenResult.claims.email;
          window.analytics.identify({
            email: email,
          });
          dispatch(UserActions.updateIsWhitelisted(isWhitelisted, email));
        });
      },
    })
  )
);

const WHITELIST_TIMEOUT_MS = 15000;

function friendlyAuthError(error) {
  if (!error) return "Something went wrong during login. Please try again.";
  const code = error.code || "";
  if (
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request"
  ) {
    return null;
  }
  if (code === "auth/popup-blocked") {
    return "Your browser blocked the login popup. Please allow popups for this site and try again.";
  }
  if (code === "auth/network-request-failed") {
    return "We couldn't reach the login service. Please check your connection and try again.";
  }
  return error.message || "Something went wrong during login. Please try again.";
}

class LoginPage extends Component {
  constructor(props) {
    super(props);
    this.state = { rejectedEmail: null, loginError: null };
    this.whitelistTimeout = null;
    const { updateCustomClaims, history, redirectTo } = props;
    history.replace(redirectTo ? `/?redirectTo=${redirectTo}` : "/");
    updateCustomClaims().catch((error) => {
      const loginError = friendlyAuthError(error);
      if (loginError) this.setState({ loginError });
    });
  }

  componentDidMount() {
    window.analytics.page("Login");
  }

  componentWillUnmount() {
    if (this.whitelistTimeout) {
      clearTimeout(this.whitelistTimeout);
      this.whitelistTimeout = null;
    }
  }

  componentDidUpdate(prevProps) {
    const { auth, isWhitelisted, whitelistEmail, firebase } = this.props;
    const currentUserIsKnownAsNotWhitelisted =
      isWhitelisted === false && auth.email === whitelistEmail;

    if (currentUserIsKnownAsNotWhitelisted && !this.state.rejectedEmail) {
      this.setState({ rejectedEmail: auth.email });
      firebase.logout();
    }

    const waitingForWhitelist =
      isLoaded(auth) &&
      !isEmpty(auth) &&
      isWhitelisted !== true &&
      isWhitelisted !== false &&
      !this.state.rejectedEmail &&
      !this.state.loginError;

    if (waitingForWhitelist && !this.whitelistTimeout) {
      this.whitelistTimeout = setTimeout(() => {
        this.whitelistTimeout = null;
        const stillWaiting =
          !isEmpty(this.props.auth) &&
          this.props.isWhitelisted !== true &&
          this.props.isWhitelisted !== false;
        if (stillWaiting) {
          this.setState({
            loginError:
              "We couldn't verify your account access. Please try again.",
          });
          firebase.logout();
        }
      }, WHITELIST_TIMEOUT_MS);
    } else if (!waitingForWhitelist && this.whitelistTimeout) {
      clearTimeout(this.whitelistTimeout);
      this.whitelistTimeout = null;
    }
  }

  loginWithGoogle() {
    const { updateCustomClaims, firebase } = this.props;
    let callback = null;
    let metadataRef = null;

    this.setState({ rejectedEmail: null, loginError: null });

    firebase.auth().onAuthStateChanged((user) => {
      // Remove previous listener
      if (callback) {
        metadataRef.off("value", callback);
      }
      // On user login add new listener
      if (user) {
        // Note: This is Google Auth's name + email, not Airtable's.
        LogRocket.identify(user.uid, {
          name: user.displayName,
          email: user.email,
        });
        // Check if whitelist refresh is required
        metadataRef = firebase
          .database()
          .ref("metadata/" + user.uid + "/refreshTime");
        callback = (snapshot) => {
          updateCustomClaims().catch((error) => {
            const loginError = friendlyAuthError(error);
            if (loginError) this.setState({ loginError });
          });
        };
        metadataRef.on("value", callback);
      }
    });

    return firebase
      .login({ provider: "google", type: "popup" })
      .catch((error) => {
        const loginError = friendlyAuthError(error);
        if (loginError) this.setState({ loginError });
      });
  }

  render() {
    const { auth } = this.props;
    const { rejectedEmail, loginError } = this.state;
    const googleButton = (
      <GoogleButton type="light" onClick={this.loginWithGoogle.bind(this)} />
    );

    let loginControls;
    if (loginError) {
      loginControls = (
        <React.Fragment>
          <p>{loginError}</p>
          <p>
            If the problem persists, email{" "}
            <a href="mailto:tech-help@joininteract.com">
              tech-help@joininteract.com
            </a>
            .
          </p>
          {googleButton}
        </React.Fragment>
      );
    } else if (!isLoaded(auth)) {
      // Auth not loaded yet
      loginControls = <span className="Spinner" />;
    } else if (rejectedEmail) {
      // User was not whitelisted — signed out, show error
      loginControls = (
        <React.Fragment>
          <p>
            {'"' + rejectedEmail + '"'} was not found in our system. Please try
            a different account, or email{" "}
            <a href="mailto:tech-help@joininteract.com">
              tech-help@joininteract.com
            </a>{" "}
            if you believe this is an error.
          </p>
          {googleButton}
        </React.Fragment>
      );
    } else if (!isEmpty(auth)) {
      // User signed in, whitelist status not yet confirmed
      loginControls = <span className="Spinner" />;
    } else {
      // User not signed in
      loginControls = googleButton;
    }

    return (
      <div className="LoginPage">
        <Navbar loggedOut={true} />
        <div className="text-content">
          <h1>
            Welcome to the <span className="title">Interact Portal</span>
          </h1>
          <div className="login-controls">{loginControls}</div>
          <p className="light">
            <a href="mailto:tech-help@joininteract.com?subject=Login Help">
              Need help?
            </a>
          </p>
        </div>
        <Footer />
      </div>
    );
  }
}

export default enhance(LoginPage);
